import { getWebsite, getWebsitePlatform } from '@va/dashboard/selectors/core';
import { useAddNotification } from '@va/dashboard/util-hooks';
import { mapPlatform } from '@va/standalone/shared/helpers';
import { withProtocol } from '@va/util/helpers';
import { useListenForMessage } from '@va/util/hooks';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

export enum IGNORE_TRACKING_VALUES {
  CHECK_TRACKING = 'checktracking',
  START_TRACKING = 'trackme',
  STOP_TRACKING = 'donttrackme',
}

/**
 * Uses iframe to check the initial ignore status
 *
 * Opens a popup window to toggle ignore status (necessary to avoid partitioned storage issues)
 */
const useIgnoreBrowserTracking = () => {
  const website = useSelector(getWebsite);
  const platform = useSelector(getWebsitePlatform);
  const platformId = mapPlatform(platform);

  const { showSuccessNotification } = useAddNotification();

  const windowRef = useRef<Window | null>(null);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  const [iframeParam, setIframeParam] = useState(IGNORE_TRACKING_VALUES.CHECK_TRACKING);
  const [isIgnoreBrowserTrackingEnabled, setIsIgnoreBrowserTrackingEnabled] = useState<boolean>(false);

  const ignoreLink = useMemo(() => {
    if (!website.url || !platformId || !website.hash) return '';

    return `${withProtocol(website.url)}?visa_${platformId}_${website.hash}=`;
  }, [platformId, website.hash, website.url]);

  const iframeLink = useMemo(() => ignoreLink + iframeParam, [ignoreLink, iframeParam]);

  const onToggle = useCallback(() => {
    const param = isIgnoreBrowserTrackingEnabled
      ? IGNORE_TRACKING_VALUES.START_TRACKING
      : IGNORE_TRACKING_VALUES.STOP_TRACKING;

    windowRef.current = window.open(
      ignoreLink + param,
      '_blank',
      'popup,width=100,height=100,left=0,top=0,toolbar=false,location=false,menubar=false,scrollbars=false',
    );
  }, [ignoreLink, isIgnoreBrowserTrackingEnabled]);

  const onMessageReceived = useCallback(
    async (message: MessageEvent) => {
      if (message?.data?.name === `VISA_IGNORE_STATUS_${website.hash}`) {
        if (message?.data?.body?.ignore !== isIgnoreBrowserTrackingEnabled)
          setIsIgnoreBrowserTrackingEnabled(message?.data?.body?.ignore ?? false);
      }

      if (message?.data?.name === 'VISA_IGNORE_STATUS_UPDATED') {
        if (message?.data?.body?.ignore !== isIgnoreBrowserTrackingEnabled) {
          setIsIgnoreBrowserTrackingEnabled(message?.data?.body?.ignore ?? isIgnoreBrowserTrackingEnabled);
          windowRef.current?.close();
          windowRef.current = null;
          showSuccessNotification(2000);

          // Because of the isolated iframe storage, we need to update the iframe ignore value
          // Otherwise, the iframe will not reflect the new ignore status
          setIframeParam(
            message?.data?.body?.ignore ? IGNORE_TRACKING_VALUES.STOP_TRACKING : IGNORE_TRACKING_VALUES.START_TRACKING,
          );
        } else {
          setIframeParam(IGNORE_TRACKING_VALUES.CHECK_TRACKING);
        }
      }
    },
    [website.hash, isIgnoreBrowserTrackingEnabled, showSuccessNotification],
  );

  useEffect(() => {
    if (!iframeRef.current && ignoreLink) {
      iframeRef.current = document.createElement('iframe');
      iframeRef.current.style.display = 'none';
      iframeRef.current.src = iframeLink;
      document.body.appendChild(iframeRef.current);
    }

    return () => {
      if (iframeRef.current) {
        document.body.removeChild(iframeRef.current);
        iframeRef.current = null;
      }
    };
  }, [iframeLink, ignoreLink]);

  useListenForMessage(onMessageReceived);

  return { isIgnoreBrowserTrackingEnabled, onToggle };
};

export default useIgnoreBrowserTracking;
